/**
 * SNS share buttons — client-side actions.
 * Imperative DOM manipulation (same pattern as scroll-header.ts).
 *
 * - Copy: navigator.clipboard.writeText + textarea fallback
 * - Native share: navigator.share (mobile only, hidden by default)
 */

const COPIED_CLASS = 'share-copied';
const FEEDBACK_MS = 2000;

/** Clipboard fallback for HTTP / older browsers */
function fallbackCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    /* ignore */
  }
  document.body.removeChild(ta);
  return ok;
}

function initCopyButton(btn: HTMLButtonElement, url: string, text: string): void {
  const label = btn.querySelector<HTMLElement>('[data-share-copy-label]');
  let feedbackTimer: ReturnType<typeof setTimeout> | undefined;

  btn.addEventListener('click', async () => {
    const copyText = `${text}\n${url}`;
    let ok = false;

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(copyText);
        ok = true;
      } catch {
        ok = fallbackCopy(copyText);
      }
    } else {
      ok = fallbackCopy(copyText);
    }

    if (ok && label) {
      clearTimeout(feedbackTimer);
      label.textContent = 'コピー済み';
      btn.classList.add(COPIED_CLASS);
      feedbackTimer = setTimeout(() => {
        label.textContent = 'リンクをコピー';
        btn.classList.remove(COPIED_CLASS);
      }, FEEDBACK_MS);
    }
  });
}

function initNativeShareButton(btn: HTMLButtonElement, url: string, text: string): void {
  if (!navigator.share) return;

  // Show the button (hidden by default in SSR)
  btn.removeAttribute('hidden');

  btn.addEventListener('click', () => {
    navigator.share({ text, url }).catch(() => {
      /* user cancelled or error — ignore */
    });
  });
}

export function initShareActions(): void {
  for (const wrapper of document.querySelectorAll<HTMLElement>('[data-share-buttons]')) {
    const url = wrapper.getAttribute('data-share-url') ?? '';
    const text = wrapper.getAttribute('data-share-text') ?? '';

    const copyBtn = wrapper.querySelector<HTMLButtonElement>('[data-share-copy]');
    if (copyBtn) initCopyButton(copyBtn, url, text);

    const nativeBtn = wrapper.querySelector<HTMLButtonElement>('[data-share-native]');
    if (nativeBtn) initNativeShareButton(nativeBtn, url, text);
  }
}
